/**
 * React Query Hooks for League Features (Web Frontend)
 */

import {useQuery, useMutation, useQueryClient, keepPreviousData} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "../../../redux/defaultStore";
import {
    LeaguesApi,
    TeamsApi,
    GetMeTeamsActiveOnlyEnum,
    GetLeagueStandingsResponse,
    RegisterTeamResponse,
    ConfirmResultResponse,
    DisputeResultResponse,
} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {
    League,
    GetLeaguesParams,
    GetLeaguesResponse,
    GetLeagueTeamsResponse,
    RegisterLeagueTeamBody,
    GetLeagueScheduleResponse,
    GetMatchResponse,
    SubmitScoreBody,
    SubmitScoreResponse,
    DisputeResultBody,
} from "../types";

// Squad types for registration
export interface Squad {
    _id: string;
    id: string;
    name: string;
    tag?: string;
    status: string;
    members: Array<{
        odId: string;
        displayName: string;
        role: string;
    }>;
}

export interface GetMySquadsResponse {
    squads: Squad[];
    paginationInfo: {
        total: number;
        limit: number;
        offset: number;
    };
}

// Query key constants
const LEAGUES_KEY = "leagues";
const LEAGUE_KEY = "league";
const LEAGUE_TEAMS_KEY = "league-teams";
const LEAGUE_STANDINGS_KEY = "league-standings";
const LEAGUE_SCHEDULE_KEY = "league-schedule";
const LEAGUE_MATCH_KEY = "league-match";
const MY_SQUADS_KEY = "my-squads";

/**
 * Hook to fetch paginated list of leagues
 */
export function useGetLeagues(params: GetLeaguesParams = {}) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [LEAGUES_KEY, params, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetLeaguesResponse> => {
            const api = new LeaguesApi(getConfig(fullToken));
            return api.getLeagues({
                limit: params.limit,
                offset: params.offset,
                status: params.status as unknown as any,
                gameId: params.gameId,
                search: params.search,
            }) as unknown as GetLeaguesResponse;
        },
        staleTime: 2 * 60000, // 2 minutes
        placeholderData: keepPreviousData,
    });
}

/**
 * Hook to fetch a single league by ID
 */
export function useGetLeague(leagueId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [LEAGUE_KEY, leagueId, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<{ league: League }> => {
            const api = new LeaguesApi(getConfig(fullToken));
            return api.getLeaguesId({ id: leagueId! });
        },
        enabled: !!leagueId,
        staleTime: 2 * 60000,
    });
}

/**
 * Hook to fetch teams for a league
 */
export function useGetLeagueTeams(leagueId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [LEAGUE_TEAMS_KEY, leagueId, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetLeagueTeamsResponse> => {
            const api = new LeaguesApi(getConfig(fullToken));
            return api.getLeaguesIdTeams({ id: leagueId! }) as unknown as GetLeagueTeamsResponse;
        },
        enabled: !!leagueId,
        staleTime: 2 * 60000,
    });
}

/**
 * Hook to fetch league standings
 */
export function useGetLeagueStandings(leagueId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [LEAGUE_STANDINGS_KEY, leagueId, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetLeagueStandingsResponse> => {
            const api = new LeaguesApi(getConfig(fullToken));
            return api.getLeaguesIdStandings({ id: leagueId! });
        },
        enabled: !!leagueId,
        staleTime: 30000, // 30 seconds for active standings
    });
}

/**
 * Hook to register a team for a league
 */
export function useRegisterTeam() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ leagueId, body }: { leagueId: string; body: RegisterLeagueTeamBody }): Promise<RegisterTeamResponse> => {
            if (!fullToken?.token) {
                throw new Error("Authentication required");
            }
            const api = new LeaguesApi(getConfig(fullToken));
            return api.createLeaguesIdRegister({
                id: leagueId,
                registerLeagueTeamBody: body,
            });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_KEY, variables.leagueId]});
            queryClient.invalidateQueries({queryKey: [LEAGUE_TEAMS_KEY, variables.leagueId]});
            queryClient.invalidateQueries({queryKey: [LEAGUES_KEY]});
        },
    });
}

/**
 * Hook to fetch current user's squads for registration
 */
export function useGetMySquads() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [MY_SQUADS_KEY, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetMySquadsResponse> => {
            const api = new TeamsApi(getConfig(fullToken));
            /*
             * Defensive fallback: compiled `@devour/client` enums have
             * intermittently evaluated to undefined at runtime (same Sentry
             * issue documented in useGameSearchInfinite.ts). Falling back to
             * the wire literal keeps behavior identical when the enum loads
             * and prevents "Cannot read properties of undefined (reading 'True')"
             * when it doesn't.
             */
            const activeOnly = (GetMeTeamsActiveOnlyEnum?.True ?? "true") as GetMeTeamsActiveOnlyEnum;
            const data = await api.getMeTeams({ activeOnly });
            return {
                squads: (data.teams ?? []) as unknown as GetMySquadsResponse["squads"],
                paginationInfo: data.paginationInfo as unknown as GetMySquadsResponse["paginationInfo"],
            };
        },
        enabled: !!fullToken?.token,
        staleTime: 5 * 60000, // 5 minutes
    });
}

/**
 * Hook to invalidate league caches
 */
export function useLeagueCacheInvalidation() {
    const queryClient = useQueryClient();

    return {
        invalidateLeague: (leagueId: string) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_KEY, leagueId]});
        },
        invalidateLeagueList: () => {
            queryClient.invalidateQueries({queryKey: [LEAGUES_KEY]});
        },
        invalidateLeagueTeams: (leagueId: string) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_TEAMS_KEY, leagueId]});
        },
        invalidateSchedule: (leagueId: string) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_SCHEDULE_KEY, leagueId]});
        },
        invalidateMatch: (leagueId: string, matchId: string) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_MATCH_KEY, leagueId, matchId]});
        },
        invalidateAll: (leagueId?: string) => {
            queryClient.invalidateQueries({queryKey: [LEAGUES_KEY]});
            if (leagueId) {
                queryClient.invalidateQueries({queryKey: [LEAGUE_KEY, leagueId]});
                queryClient.invalidateQueries({queryKey: [LEAGUE_TEAMS_KEY, leagueId]});
                queryClient.invalidateQueries({queryKey: [LEAGUE_STANDINGS_KEY, leagueId]});
                queryClient.invalidateQueries({queryKey: [LEAGUE_SCHEDULE_KEY, leagueId]});
            }
        },
    };
}

/**
 * Hook to fetch league schedule
 */
export function useGetLeagueSchedule(leagueId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [LEAGUE_SCHEDULE_KEY, leagueId, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetLeagueScheduleResponse> => {
            const api = new LeaguesApi(getConfig(fullToken));
            return api.getLeaguesIdSchedule({ id: leagueId! }) as unknown as GetLeagueScheduleResponse;
        },
        enabled: !!leagueId,
        staleTime: 60000, // 1 minute
    });
}

/**
 * Hook to fetch single match details
 */
export function useGetLeagueMatch(leagueId: string | undefined, matchId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [LEAGUE_MATCH_KEY, leagueId, matchId, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetMatchResponse> => {
            const api = new LeaguesApi(getConfig(fullToken));
            return api.getLeaguesIdMatchesMatchId({ id: leagueId!, matchId: matchId! }) as unknown as GetMatchResponse;
        },
        enabled: !!leagueId && !!matchId,
        staleTime: 30000, // 30 seconds
    });
}

/**
 * Hook to submit score for a match
 */
export function useSubmitScore() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            leagueId,
            matchId,
            body,
        }: {
            leagueId: string;
            matchId: string;
            body: SubmitScoreBody;
        }): Promise<SubmitScoreResponse> => {
            if (!fullToken?.token) {
                throw new Error("Authentication required");
            }
            const api = new LeaguesApi(getConfig(fullToken));
            return api.createLeaguesIdMatchesMatchIdSubmitResult({
                id: leagueId,
                matchId,
                submitResultBody: body,
            }) as unknown as SubmitScoreResponse;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_SCHEDULE_KEY, variables.leagueId]});
            queryClient.invalidateQueries({queryKey: [LEAGUE_MATCH_KEY, variables.leagueId, variables.matchId]});
        },
    });
}

/**
 * Hook to confirm match result
 */
export function useConfirmResult() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ leagueId, matchId }: { leagueId: string; matchId: string }): Promise<ConfirmResultResponse> => {
            if (!fullToken?.token) {
                throw new Error("Authentication required");
            }
            const api = new LeaguesApi(getConfig(fullToken));
            return api.updateLeaguesIdMatchesMatchIdConfirm({ id: leagueId, matchId });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_SCHEDULE_KEY, variables.leagueId]});
            queryClient.invalidateQueries({queryKey: [LEAGUE_MATCH_KEY, variables.leagueId, variables.matchId]});
            queryClient.invalidateQueries({queryKey: [LEAGUE_KEY, variables.leagueId]});
            queryClient.invalidateQueries({queryKey: [LEAGUE_STANDINGS_KEY, variables.leagueId]});
        },
    });
}

/**
 * Hook to dispute match result
 */
export function useDisputeResult() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            leagueId,
            matchId,
            body,
        }: {
            leagueId: string;
            matchId: string;
            body: DisputeResultBody;
        }): Promise<DisputeResultResponse> => {
            if (!fullToken?.token) {
                throw new Error("Authentication required");
            }
            const api = new LeaguesApi(getConfig(fullToken));
            return api.createLeaguesIdMatchesMatchIdDispute({
                id: leagueId,
                matchId,
                disputeResultBody: body,
            });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: [LEAGUE_SCHEDULE_KEY, variables.leagueId]});
            queryClient.invalidateQueries({queryKey: [LEAGUE_MATCH_KEY, variables.leagueId, variables.matchId]});
        },
    });
}
